import { useState, useEffect, useCallback } from 'react';
import { getContacts, deleteContact, updateContact } from '../../lib/api/adminApi';

const SERVICES = ['Plomberie', 'Chauffage', 'Climatisation', 'Salle de bain clé en main', 'Pompe à chaleur', 'Autre'];

const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const ServiceBadge = ({ s }) => {
  const colors = { Plomberie: 'blue', Chauffage: 'yellow', Climatisation: 'blue', 'Salle de bain clé en main': 'green', 'Pompe à chaleur': 'green', Autre: 'yellow' };
  return <span className={`admin-badge admin-badge-${colors[s] ?? 'blue'}`}>{s}</span>;
};

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getContacts();
      setContacts(data.sort((a, b) => new Date(b.dateEnvoi) - new Date(a.dateEnvoi)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    await deleteContact(id);
    setConfirmDelete(null);
    setContacts(prev => prev.filter(c => c.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateContact({
        id: editing.id,
        prenom: editing.prenom,
        nom: editing.nom,
        email: editing.email,
        telephone: editing.telephone,
        service: editing.service,
        message: editing.message,
      });
      setContacts(prev => prev.map(c => c.id === editing.id ? { ...c, ...editing } : c));
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-spinner">Chargement...</div>;

  return (
    <>
      <div className="admin-page-title">Demandes de contact</div>
      <div className="admin-page-sub">{contacts.length} demande{contacts.length !== 1 ? 's' : ''} reçue{contacts.length !== 1 ? 's' : ''}</div>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Service</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 && (
              <tr><td colSpan={5} className="admin-empty">Aucune demande pour l'instant.</td></tr>
            )}
            {contacts.map(c => (
              <tr key={c.id}>
                <td><strong>{c.prenom} {c.nom}</strong></td>
                <td>{c.email}</td>
                <td><ServiceBadge s={c.service} /></td>
                <td style={{ color: '#6b7280', fontSize: '0.82rem' }}>{fmtDate(c.dateEnvoi)}</td>
                <td>
                  <button className="admin-action-btn view" onClick={() => setSelected(c)}>👁 Voir</button>
                  <button className="admin-action-btn edit" onClick={() => setEditing({ ...c })}>✏️ Modifier</button>
                  <button className="admin-action-btn delete" onClick={() => setConfirmDelete(c)}>🗑 Supprimer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL VOIR */}
      {selected && (
        <div className="admin-modal-overlay" onClick={() => setSelected(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">Détail de la demande</div>
              <button className="admin-modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-detail-row"><span className="admin-detail-label">Prénom</span><span className="admin-detail-value">{selected.prenom}</span></div>
              <div className="admin-detail-row"><span className="admin-detail-label">Nom</span><span className="admin-detail-value">{selected.nom}</span></div>
              <div className="admin-detail-row"><span className="admin-detail-label">Email</span><span className="admin-detail-value"><a href={`mailto:${selected.email}`} style={{ color: '#16a34a' }}>{selected.email}</a></span></div>
              <div className="admin-detail-row"><span className="admin-detail-label">Téléphone</span><span className="admin-detail-value">{selected.telephone || '—'}</span></div>
              <div className="admin-detail-row"><span className="admin-detail-label">Service</span><span className="admin-detail-value"><ServiceBadge s={selected.service} /></span></div>
              <div className="admin-detail-row"><span className="admin-detail-label">Date</span><span className="admin-detail-value">{fmtDate(selected.dateEnvoi)}</span></div>
              <div style={{ marginTop: 16 }}>
                <div className="admin-form-label" style={{ marginBottom: 8 }}>Message</div>
                <div className="admin-msg-detail">{selected.message}</div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <a href={`mailto:${selected.email}?subject=Re: votre demande Tramenergie`} className="admin-btn admin-btn-primary" style={{ textDecoration: 'none' }}>
                Répondre par email
              </a>
              <button className="admin-btn admin-btn-secondary" onClick={() => setSelected(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MODIFIER */}
      {editing && (
        <div className="admin-modal-overlay" onClick={() => setEditing(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">Modifier la demande</div>
              <button className="admin-modal-close" onClick={() => setEditing(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              {['prenom', 'nom', 'email', 'telephone'].map(field => (
                <div className="admin-form-group" key={field}>
                  <label className="admin-form-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <input
                    className="admin-form-input"
                    value={editing[field] ?? ''}
                    onChange={e => setEditing(prev => ({ ...prev, [field]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="admin-form-group">
                <label className="admin-form-label">Service</label>
                <select className="admin-form-input" value={editing.service ?? ''} onChange={e => setEditing(prev => ({ ...prev, service: e.target.value }))}>
                  {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Message</label>
                <textarea className="admin-form-input" value={editing.message ?? ''} onChange={e => setEditing(prev => ({ ...prev, message: e.target.value }))} />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setEditing(null)}>Annuler</button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRIMER */}
      {confirmDelete && (
        <div className="admin-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="admin-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">Confirmer la suppression</div>
              <button className="admin-modal-close" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <p style={{ color: '#374151', fontSize: '0.9rem' }}>
                Supprimer la demande de <strong>{confirmDelete.prenom} {confirmDelete.nom}</strong> ({confirmDelete.email}) ?
                Cette action est irréversible.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(confirmDelete.id)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminContacts;
